import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/agent_login.py's AgentLoginOut. No `password` (or hash)
// field — the backend never returns one here, by design.
export interface AgentLogin {
  agent_user_id: number;
  username: string;
  display_name: string | null;
  extension_id: number | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

// Mirrors AgentLoginList. `used`/`cap` ride alongside the rows deliberately:
// the cap counts DEACTIVATED logins too (usernames are never recycled — see
// migration 0044), so a screen that only counted the visible active rows
// would leave an operator staring at a 409 with no way to see why.
export interface AgentLoginList {
  agents: AgentLogin[];
  used: number;
  cap: number;
}

export interface AgentLoginCreate {
  username: string;
  password: string;
  display_name?: string;
  extension_id?: number;
}

export interface AgentLoginPatch {
  display_name?: string;
  extension_id?: number;
  is_active?: boolean;
  unbind_seat?: boolean;
}

// No trailing slash mismatch: the backend route is mounted at
// prefix="/api/agent-logins" with a bare "/" for list/create (same pattern as
// /extensions/), so a trailing slash here avoids a 307 that can drop the auth
// header through the proxy.
export async function listAgentLogins(): Promise<AgentLoginList> {
  const resp = await api.get<APIResponse<AgentLoginList>>("/agent-logins/");
  return unwrap(resp.data);
}

export async function createAgentLogin(body: AgentLoginCreate): Promise<AgentLogin> {
  const resp = await api.post<APIResponse<AgentLogin>>("/agent-logins/", body);
  return unwrap(resp.data);
}

export async function patchAgentLogin(id: number, body: AgentLoginPatch): Promise<void> {
  await api.patch(`/agent-logins/${id}`, body);
}

export async function resetAgentPassword(id: number, password: string): Promise<void> {
  await api.post(`/agent-logins/${id}/password`, { password });
}

// The full stand-down (Resolved Decisions #5, 2026-07-30 agent-logins plan):
// kills the agent's live token, logs their seat out of presence, and clears
// its heartbeat key. Not a soft "sign out" — it is meant to be confirmed
// before firing, which is why this is a separate call from `patchAgentLogin`
// rather than folded into `is_active`.
export async function forceLogoutAgentLogin(id: number): Promise<void> {
  await api.post(`/agent-logins/${id}/force-logout`);
}

// --- One-step provisioning -------------------------------------------------
// Mirrors app/routers/agent_login.py AgentProvision / AgentProvisioned. Seat +
// sign-in + queue assignments in one request and one transaction: a failure
// anywhere leaves nothing behind, which is the difference from creating the
// three by hand in sequence.

export interface AgentProvisionInput {
  display_name: string;
  username: string;
  password: string;
  /** The extension number to use; omit to have one allocated. */
  extension_name?: string;
  campaign_ids?: number[];
}

export interface AgentProvisioned {
  extension_id: number;
  extension_name: string;
  title: string;
  agent_user_id: number;
  username: string;
  campaign_ids: number[];
}

export async function provisionAgent(body: AgentProvisionInput): Promise<AgentProvisioned> {
  const resp = await api.post<APIResponse<AgentProvisioned>>("/agent-logins/provision", body);
  return unwrap(resp.data);
}
